import React, { useState } from "react";

import { Text } from "../../../components/Text";
import { Button, Icon } from "../../../components/tailwind";

export type SectionChannelSettingsValue = {
  [key: string]: string | boolean | undefined;
};

type Props = {
  settings: SectionChannelSettingsValue;
  onChange: (next: SectionChannelSettingsValue) => void;
  disabled?: boolean;
};

type FieldType = "string" | "boolean";

type FieldDef = {
  key: string;
  label: string;
  type: FieldType;
  description?: string;
  placeholder?: string;
};

const FIELDS: FieldDef[] = [
  {
    key: "name",
    label: "Name",
    type: "string",
    description: "Rename the section in the output (data-textual-section).",
  },
  {
    key: "section_tag",
    label: "Section tag",
    type: "string",
    placeholder: "div",
    description: "HTML tag used to wrap the section (default: div).",
  },
  {
    key: "html",
    label: "HTML",
    type: "boolean",
    description: "Mark section content as HTML.",
  },
  {
    key: "list_item",
    label: "List item",
    type: "string",
    description: "List item tag, e.g. 'li'.",
  },
  {
    key: "key",
    label: "Key",
    type: "string",
    description: "Map section to an external key name.",
  },
  {
    key: "untranslated",
    label: "Untranslated",
    type: "boolean",
    description: "Mark the section as untranslated.",
  },
  {
    key: "titleize",
    label: "Titleize",
    type: "boolean",
    description: "Uppercase every first letter.",
  },
  {
    key: "add_period",
    label: "Add period",
    type: "boolean",
    description: "Turn on to add a period at the end of the text.",
  },
  {
    key: "bullet",
    label: "Bullet list",
    type: "boolean",
    description: "Wrap items in <ul><li>.",
  },
  {
    key: "item_separator",
    label: "Item separator",
    type: "string",
    description: "Separator between items, e.g. '<br>'.",
  },
  {
    key: "section_separator",
    label: "Section separator",
    type: "string",
    description: "Separator after section, overrides global.",
  },
];

const updateField = (
  current: SectionChannelSettingsValue,
  key: string,
  value: string | boolean
): SectionChannelSettingsValue => {
  const next = { ...current };
  if (key === "add_period" && value === false) {
    next[key] = false;
    return next;
  }
  if (value === "" || value === false) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next;
};

export const SectionChannelSettings: React.FC<Props> = ({
  settings,
  onChange,
  disabled,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="tw-mt-4" data-testid="section-channel-settings">
      <Button
        type="button"
        variant="transparent"
        borderless
        className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-bg-transparent tw-p-0 tw-text-left"
        data-testid="section-channel-settings-toggle"
        onClick={(): void => setOpen((v) => !v)}
      >
        <Icon
          name={open ? "expand_more" : "chevron_right"}
          className="tw-text-lg"
        />
        <Text as="h5" compact inline>
          Channel settings
        </Text>
        <Text color="grey" size="small" inline>
          Per-section settings stored on the connected channel (replaces raw
          JSON in admin).
        </Text>
      </Button>
      {open && (
        <div className="tw-mt-3 tw-space-y-4">
          {FIELDS.map((field) => {
            const inputId = `section-channel-settings-${field.key}`;
            const value = settings[field.key];
            if (field.type === "boolean") {
              return (
                <div
                  key={field.key}
                  data-testid={`section-channel-settings-field-${field.key}`}
                  className="tw-space-y-1"
                >
                  <label
                    htmlFor={inputId}
                    className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm"
                  >
                    <input
                      id={inputId}
                      data-testid={`section-channel-settings-input-${field.key}`}
                      type="checkbox"
                      disabled={disabled}
                      checked={
                        field.key === "add_period"
                          ? value === true
                          : Boolean(value)
                      }
                      className="tw-h-4 tw-w-4"
                      onChange={(event): void =>
                        onChange(
                          updateField(settings, field.key, event.target.checked)
                        )
                      }
                    />
                    <span>{field.label}</span>
                  </label>
                  {field.description ? (
                    <Text color="grey" size="small" compact>
                      {field.description}
                    </Text>
                  ) : null}
                </div>
              );
            }
            return (
              <div
                key={field.key}
                data-testid={`section-channel-settings-field-${field.key}`}
                className="tw-space-y-1"
              >
                <label htmlFor={inputId} className="tw-block tw-text-sm">
                  {field.label}
                </label>
                <input
                  id={inputId}
                  data-testid={`section-channel-settings-input-${field.key}`}
                  type="text"
                  disabled={disabled}
                  placeholder={field.placeholder}
                  value={typeof value === "string" ? value : ""}
                  className="tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-transparent focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 disabled:tw-opacity-50"
                  onChange={(event): void =>
                    onChange(
                      updateField(settings, field.key, event.target.value)
                    )
                  }
                />
                {field.description ? (
                  <Text color="grey" size="small" compact>
                    {field.description}
                  </Text>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
